import { assign, raise, sendParent, setup, assertEvent } from 'xstate';
import { openClientActor, viewClientsActor } from './client.actors';
import type { ClientEvent } from './events';
import type { ViewClientsQueryQuery } from '@/lib/gql/__generated__/graphql';

type Client = ViewClientsQueryQuery['viewClients'][number];

type ClientContext = {
  items: Client[];
  error: string | null;
};

const genericReadError = 'Clients could not be loaded.';
const genericWriteError = 'The Client could not be opened.';

export const clientMachine = setup({
  types: {
    context: {} as ClientContext,
    events: {} as ClientEvent | { type: 'client.changed' },
  },
  actors: {
    viewClients: viewClientsActor,
    openClient: openClientActor,
  },
}).createMachine({
  id: 'client',
  type: 'parallel',
  context: { items: [], error: null },
  states: {
    viewClients: {
      initial: 'idle',
      on: { 'client.load-viewClients': '.loading' },
      states: {
        idle: {},
        loading: {
          invoke: {
            id: 'viewClients',
            src: 'viewClients',
            onDone: { target: 'ready', actions: assign({ items: ({ event }) => event.output, error: () => null }) },
            onError: { target: 'error', actions: assign({ error: () => genericReadError }) },
          },
        },
        ready: { on: { 'client.changed': 'loading' } },
        error: { on: { 'client.load-viewClients': 'loading', 'client.changed': 'loading' } },
      },
    },
    write: {
      initial: 'idle',
      on: { 'client.open': '.saving' },
      states: {
        idle: {},
        saving: {
          invoke: {
            id: 'openClient',
            src: 'openClient',
            input: ({ event }) => {
              assertEvent(event, 'client.open');
              return { input: event.input };
            },
            onDone: [
              {
                guard: ({ event }) => event.output.success,
                target: 'idle',
                actions: [
                  assign({ error: () => null }),
                  raise({ type: 'client.changed' }),
                  sendParent({ type: 'client.opened' }),
                  sendParent({ type: 'ui.overlay.close', name: 'openClientDialog' }),
                  sendParent({ type: 'ui.toast.push', toast: { title: 'Client opened', tone: 'success' } }),
                ],
              },
              {
                target: 'idle',
                actions: [
                  assign({ error: ({ event }) => event.output.error?.message ?? genericWriteError }),
                  sendParent({ type: 'ui.toast.push', toast: { title: 'Client could not be opened', tone: 'error' } }),
                ],
              },
            ],
            onError: {
              target: 'idle',
              actions: [
                assign({ error: () => genericWriteError }),
                sendParent({ type: 'ui.toast.push', toast: { title: 'Client could not be opened', tone: 'error' } }),
              ],
            },
          },
        },
      },
    },
  },
  on: {
    'client.open-dialog': { actions: sendParent({ type: 'ui.overlay.open', name: 'openClientDialog' }) },
  },
});
